<script setup lang="ts">
const pageTitle = "Donate today";
useHead({
  title: pageTitle,
});

const { data: causes } = useAsyncData(
  () =>
    useDirectus(
      readItems("causes", {
        fields: ["*"],
        filter: {
          status: { _eq: "published" },
        },
      })
    ),
  { default: () => [] }
);

const { data: page } = useAsyncData(() =>
  useDirectus(
    readItems("generic_pages", {
      fields: ["*"],
      filter: {
        status: { _eq: "published" },
        slug: { _eq: "donate" },
      },
    })
  ).then((results) => (results.length ? results[0] : null))
);

const groupsColSpan = computed(() => {
  const remainder = (causes.value?.length ?? 0) % 3;
  if (remainder === 2) return "md:col-span-1";
  if (remainder === 1) return "md:col-span-2";
  return "md:col-span-3";
});
</script>

<template>
  <div class="bg-gray">
    <CommonPageBanner
      :image="page?.banner_image ? page.banner_image : '/mountains-banner-image.jpg'"
      :local-asset="page?.banner_image ? false : true"
      size="small"
    >
      <h1 class="text-4xl font-semibold md:text-6xl xl:text-banner">
        {{ pageTitle }}
      </h1>
      <p class="text-lg">Join thousands of Tasmanians protecting our wild places, wildlife, and cultural heritage.</p>
    </CommonPageBanner>

    <CommonContainer class="pb-[--y-margin] pt-16">
      <div class="space-y-4 pb-8">
        <h3 class="mx-auto text-center text-2xl font-semibold text-accentDark md:text-3xl xl:max-w-[80%] xl:text-4xl">
          Our mission is to support communities and volunteers to contribute to conservation actions that ensure the long-term protection of
          Tasmania's natural environment.
        </h3>
        <Wysiwyg v-if="page?.content" :content="page?.content || ''" class="mb-8" />
        <p class="mx-auto text-center text-xl">
          Every dollar you give helps protect Tasmania's wild places and supports the people working to care for them.
        </p>
        <p class="mx-auto text-center text-lg font-semibold">Choose where your gift makes its impact:</p>
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-0 md:overflow-hidden md:rounded-xl">
        <NuxtLink
          v-for="(cause, index) in causes"
          :key="cause.id"
          :to="`/donate/${cause.slug}`"
          class="group flex flex-col items-center gap-5 rounded-xl p-12 text-center md:rounded-none"
          :class="index % 2 === 0 ? 'bg-primary text-white' : 'bg-tertiary text-accentDark'"
        >
          <div v-if="cause.banner_image" class="size-32 shrink-0 overflow-hidden rounded-full md:size-48 xl:size-64">
            <CommonDirectusImage :img="cause.banner_image as string" class="size-full object-cover" />
          </div>
          <div class="flex flex-col gap-5">
            <h3 class="text-3xl font-semibold leading-tight">{{ cause.name }}</h3>
            <div v-if="cause.description" v-html="cause.description" class="text-lg leading-normal"></div>
          </div>
          <span
            class="mt-auto rounded-full px-8 py-5 text-base font-semibold uppercase text-accentDark transition-transform duration-200 group-hover:scale-105"
            :class="index % 2 === 0 ? 'bg-tertiary' : 'bg-white'"
          >
            Donate Now
          </span>
        </NuxtLink>

        <NuxtLink
          :to="{ path: '/groups', query: { donations: '1' } }"
          class="group flex flex-col items-center gap-5 rounded-xl bg-primary p-12 text-center text-white md:rounded-none"
          :class="groupsColSpan"
        >
          <div class="size-32 shrink-0 overflow-hidden rounded-full md:size-48 xl:size-64">
            <img src="/branch_emails.jpg" alt="" class="size-full object-cover" />
          </div>
          <div class="flex flex-col gap-5">
            <h3 class="text-3xl font-semibold leading-tight">Support a Wildcare Group</h3>
            <p class="text-lg leading-normal">
              Wildcare's volunteer groups are active across Tasmania on reserves and offshore islands, working in partnerships to restore
              important habitats, monitor at-risk wildlife, remove weeds, maintain bushwalking tracks, and preserve cultural heritage
              values.
              <br /><br />
              You can now make a tax-deductible donation to Wildcare Tasmania, directing your support to eligible Wildcare groups.
              <br /><br />
              Your tax-deductible donation will support your chosen Wildcare group in taking action to protect the natural and cultural
              values, places and wildlife you care about.
            </p>
          </div>
          <span
            class="mt-auto rounded-full bg-tertiary px-8 py-5 text-base font-semibold uppercase leading-normal text-accentDark transition-transform duration-200 group-hover:scale-105"
          >
            View Groups
          </span>
        </NuxtLink>
      </div>
    </CommonContainer>
  </div>
</template>
